package br.com.texeratx.boardgametimeapp.ui.screens.active_match

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material3.Button
import androidx.compose.material3.ElevatedCard
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.FilledTonalButton
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import br.com.texeratx.boardgametimeapp.core.i18n.LocalStrings
import br.com.texeratx.boardgametimeapp.core.active_match.ActiveMatchController
import br.com.texeratx.boardgametimeapp.core.active_match.ActiveMatchState
import br.com.texeratx.boardgametimeapp.ui.components.GameThumb
import br.com.texeratx.boardgametimeapp.ui.components.PlayerAvatar
import br.com.texeratx.boardgametimeapp.ui.screens.games.GameCover
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.koin.getScreenModel
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import org.koin.compose.getKoin
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppScreenScaffold
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppTopBarColors
import kotlin.math.round

class ActiveMatchScreen : Screen {

    @Composable
    override fun Content() {
        val navigator = LocalNavigator.currentOrThrow
        val controller = getKoin().get<ActiveMatchController>()
        val statsViewModel = getScreenModel<ActiveMatchStatsViewModel>()
        val state by controller.state.collectAsState()
        val statsState by statsViewModel.state.collectAsState()

        LaunchedEffect(state) {
            when (val current = state) {
                is ActiveMatchState.Running -> statsViewModel.loadForGame(current.match.gameId)
                is ActiveMatchState.Paused -> statsViewModel.loadForGame(current.match.gameId)
                else -> Unit
            }
        }

        ActiveMatchUI(
            state = state,
            statsState = statsState,
            onBack = { navigator.pop() },
            onPause = controller::pause,
            onResume = controller::resume,
            onFinish = {
                controller.finish()
                navigator.replace(MatchScoreScreen())
            },
            onClear = controller::clear
        )
    }
}

@Composable
private fun MatchHeaderCard(
    gameName: String,
    gameImageUrl: String?
) {
    ElevatedCard(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(18.dp)
    ) {
        Row(
            modifier = Modifier.padding(12.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (!gameImageUrl.isNullOrBlank()) {
                GameThumb(
                    imageUrl = gameImageUrl,
                    contentDescription = gameName,
                    modifier = Modifier
                        .size(width = 64.dp, height = 80.dp)
                        .clip(RoundedCornerShape(14.dp))
                )
            } else {
                GameCover(
                    imageUrl = null,
                    title = gameName,
                    modifier = Modifier
                        .size(width = 64.dp, height = 80.dp)
                        .clip(RoundedCornerShape(14.dp))
                )
            }

            Spacer(Modifier.width(12.dp))

            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = gameName,
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold
                )
                val S = LocalStrings.current
                Text(
                    text = S.matchInProgress,
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun ActiveMatchUI(
    state: ActiveMatchState,
    statsState: ActiveMatchStatsUiState,
    onBack: () -> Unit,
    onPause: () -> Unit,
    onResume: () -> Unit,
    onFinish: () -> Unit,
    onClear: () -> Unit
) {
    val S = LocalStrings.current

    AppScreenScaffold(
        topBar = {
            TopAppBar(
                title = { Text(S.activeMatchTitle, fontWeight = FontWeight.Bold) },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.Default.ArrowBack, contentDescription = S.back)
                    }
                },
                colors = AppTopBarColors()
            )
        }
    ) { padding ->

        Column(
            modifier = Modifier
                .padding(padding)
                .padding(16.dp)
                .fillMaxSize()
                .verticalScroll(rememberScrollState()),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            when (state) {
                is ActiveMatchState.Idle -> {
                    Text(S.noActiveMatch)
                    Button(onClick = onBack, modifier = Modifier.fillMaxWidth()) {
                        Text(S.back)
                    }
                }

                is ActiveMatchState.Running -> {
                    MatchHeaderCard(
                        gameName = state.match.gameName,
                        gameImageUrl = state.match.gameImageUrl
                    )

                    Spacer(Modifier.height(10.dp))

                    Text(
                        text = state.formattedElapsed,
                        style = MaterialTheme.typography.displayLarge,
                        fontWeight = FontWeight.ExtraBold
                    )

                    Spacer(Modifier.height(10.dp))

                    PlayersRow(
                        names = state.match.playerNames,
                        imageUrls = state.match.playerImageUrls,
                        firstPlayerIndex = state.match.firstPlayerIndex
                    )

                    Spacer(Modifier.height(10.dp))

                    ExpansionsBlock(state.match.expansionNames)

                    Spacer(Modifier.height(10.dp))

                    GameStatsBlock(statsState)

                    Spacer(Modifier.height(8.dp))

                    Column(
                        modifier = Modifier.navigationBarsPadding(),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        FilledTonalButton(
                            onClick = onPause,
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Text(S.pause)
                        }

                        OutlinedButton(
                            onClick = onFinish,
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Text(S.endMatch)
                        }
                    }
                }

                is ActiveMatchState.Paused -> {
                    MatchHeaderCard(
                        gameName = state.match.gameName,
                        gameImageUrl = state.match.gameImageUrl
                    )

                    Spacer(Modifier.height(10.dp))

                    Text(
                        text = state.formattedElapsed,
                        style = MaterialTheme.typography.displayLarge,
                        fontWeight = FontWeight.ExtraBold
                    )

                    Text(
                        text = S.statusPaused,
                        style = MaterialTheme.typography.labelLarge,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )

                    Spacer(Modifier.height(10.dp))

                    PlayersRow(
                        names = state.match.playerNames,
                        imageUrls = state.match.playerImageUrls,
                        firstPlayerIndex = state.match.firstPlayerIndex
                    )

                    Spacer(Modifier.height(10.dp))

                    ExpansionsBlock(state.match.expansionNames)

                    Spacer(Modifier.height(10.dp))

                    GameStatsBlock(statsState)

                    Spacer(Modifier.height(8.dp))

                    Column(
                        modifier = Modifier.navigationBarsPadding(),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        Button(
                            onClick = onResume,
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Text(S.resume)
                        }

                        OutlinedButton(
                            onClick = onClear,
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Text(S.cancelMatch)
                        }
                    }
                }

                is ActiveMatchState.Finished -> {
                    Text(S.matchFinished)
                    Text(
                        text = state.formattedElapsed,
                        style = MaterialTheme.typography.displayMedium,
                        fontWeight = FontWeight.Bold
                    )
                    Button(onClick = onBack, modifier = Modifier.fillMaxWidth()) {
                        Text(S.back)
                    }
                }
            }
        }
    }
}

@Composable
private fun GameStatsBlock(statsState: ActiveMatchStatsUiState) {
    val S = LocalStrings.current

    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Text(
            text = S.sectionCurrentGameStats,
            style = MaterialTheme.typography.titleMedium,
            fontWeight = FontWeight.SemiBold
        )

        ElevatedCard(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(16.dp)
        ) {
            Column(
                modifier = Modifier.padding(12.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                when {
                    statsState.isLoading -> Text(
                        S.loading,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )

                    statsState.stats == null -> {
                        val message = statsState.error ?: S.noStatsForGameYet
                        Text(
                            text = message,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }

                    else -> {
                        val stats = statsState.stats
                        StatsRow(S.bestScoreForGame, formatScore(stats?.bestScore))
                        StatsRow(S.averageScoreForGame, formatScore(stats?.averageScore))
                        StatsRow(S.worstWinningScoreForGame, formatScore(stats?.worstWinningScore))
                        StatsRow(
                            label = S.topWinnerForGame,
                            value = stats?.topWinnerName?.let { winnerName ->
                                "$winnerName (${S.winsForPlayerFormat(stats.topWinnerWins)})"
                            } ?: "-"
                        )
                        stats?.avgDurationMin?.let { StatsRow(S.avgDurationForGame, formatDuration(it)) }
                        stats?.minDurationMin?.let { StatsRow(S.minDurationForGame, formatDuration(it)) }
                        stats?.maxDurationMin?.let { StatsRow(S.maxDurationForGame, formatDuration(it)) }
                    }
                }
            }
        }
    }
}

@Composable
private fun StatsRow(
    label: String,
    value: String
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(
            text = label,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        Text(
            text = value,
            style = MaterialTheme.typography.bodyMedium,
            fontWeight = FontWeight.SemiBold
        )
    }
}

private fun formatDuration(minutes: Int): String {
    val h = minutes / 60
    val m = minutes % 60
    return if (h > 0) "${h}h ${m}min" else "${m}min"
}

private fun formatScore(value: Double?): String {
    if (value == null) return "-"
    val rounded = round(value * 10.0) / 10.0
    return if (rounded % 1.0 == 0.0) {
        rounded.toInt().toString()
    } else {
        rounded.toString()
    }
}

@Composable
private fun PlayersRow(
    names: List<String>,
    imageUrls: List<String?>,
    firstPlayerIndex: Int
) {
    val S = LocalStrings.current

    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Text(
            text = S.sectionPlayers,
            style = MaterialTheme.typography.titleMedium,
            fontWeight = FontWeight.SemiBold
        )

        LazyRow(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
            itemsIndexed(names) { index, name ->
                val url = imageUrls.getOrNull(index)
                val isFirst = index == firstPlayerIndex

                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    Box {
                        PlayerAvatar(
                            imageUrl = url,
                            contentDescription = name,
                            modifier = Modifier.size(44.dp)
                        )

                        if (isFirst) {
                            Text(
                                text = S.positionFirst,
                                style = MaterialTheme.typography.labelSmall,
                                color = MaterialTheme.colorScheme.onPrimary,
                                modifier = Modifier
                                    .align(Alignment.TopEnd)
                                    .padding(2.dp)
                                    .clip(RoundedCornerShape(10.dp))
                                    .background(MaterialTheme.colorScheme.primary)
                                    .padding(horizontal = 6.dp, vertical = 2.dp)
                            )
                        }
                    }

                    Spacer(Modifier.height(4.dp))
                    Text(text = name, style = MaterialTheme.typography.labelSmall, maxLines = 1)

                    if (isFirst) {
                        Text(
                            text = S.firstPlayer,
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.primary
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun ExpansionsBlock(expansionNames: List<String>) {
    val S = LocalStrings.current

    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Text(
            text = S.sectionExpansions,
            style = MaterialTheme.typography.titleMedium,
            fontWeight = FontWeight.SemiBold
        )

        if (expansionNames.isEmpty()) {
            Text(S.none, color = MaterialTheme.colorScheme.onSurfaceVariant)
        } else {
            // ✅ versão simples (sempre compila)
            Text(expansionNames.joinToString(", "))
        }
    }
}

